// ============================================================================
// DEMO BUSINESS IDENTITY: what the browser prints for a demo business
// (W-DEMO, DEMO-SPEC section 2.4, slice S3)
// ----------------------------------------------------------------------------
// Several surfaces were written when RJ Bird Building was the only business
// and print its name, ABN, licence, "RBB" prefix, people or admin address as
// a literal. A demo business shown in public must print its own profile
// values instead (services/demo-business.ts). RJ Bird's output, once its
// profile is known, does not change by a byte, apart from the logo its Stage
// 2 report, costings header and Work Method gained (G7).
//
// The profile is read from the tenant-resolver cache, which the app warms at
// start. A demo whose profile has not loaded yet gets EMPTY values, never RJ
// Bird's: a blank is a visible gap, RJ Bird's name on a demo is a leak.
// The business-name fallback and the Run Sheet search tip also give any other
// business (not RJ Bird, not a demo) its own name and Quote # (F4, D5).
//
// RJ Bird's values go only to RJ Bird, recognised by its stored business name
// (isRjBirdTenantProfile), never by its RBB prefix: another business's
// initials can also be RBB (F11). The profile counts when it is cached, or
// when it is the last one this business read successfully, which covers the
// gap after a Settings save. A business that is not known yet gets the
// neutral text and no name, never RJ Bird's. The Stage 2 report and the
// costings header print the business's own name, ABN and licence (F8), RJ
// Bird's read from its profile too (G4); those two and the Work Method show
// the logo the business's quote prints (G7). Interface copy shows RJ Bird's
// examples only to RJ Bird (F10).
//
// A profile counts only when it belongs to the signed-in business, the same
// check as business-warranty-note.ts: getInstanceOwner() serves its cache for
// five minutes whoever is signed in, so after a same-tab switch the cache can
// still hold RJ Bird's row.
// ============================================================================

import { isDemoBusiness } from './demo-business';
import { getActiveTenantId, getInstanceOwnerCached } from './tenant-resolver';
import { activeQuoteNumberPrefix, getSessionQuoteNumberPrefix } from './business-quote-prefix';
import { isRjBirdTenantProfile, normaliseTenantIdentityName } from './rj-bird-rate-card-sync';
import { businessLogoSrc, quoteAccentFor } from '../utils/quote-letterhead';

export const RJ_BIRD_FALLBACK_BUSINESS_NAME = 'RJ Bird Building';

export const RUN_SHEET_SEARCH_TIP_RJ_BIRD = 'Try "SP 3460", "RBB260614", a street name, or a client. Press / anywhere to search.';
const RUN_SHEET_SEARCH_TIP_DEMO = 'Try a strata plan, a Quote #, a street name, or a client. Press / anywhere to search.';
/** Another business's tip: its own Quote # as the example, no RJ Bird strata plan. */
const runSheetSearchTipFor = (prefix: string) => `Try a strata plan, "${prefix}260614", a street name, or a client. Press / anywhere to search.`;

/** The same rule as tenant_profiles.quote_number_prefix (migration 091). */
const PREFIX_RE = /^[A-Za-z0-9]{1,8}$/;

export interface DemoBusinessIdentity {
    /** Legal name, or the trading name when no legal name is set. */
    legalName: string;
    /** Trading name, or the legal name when no trading name is set. */
    tradingName: string;
    abn: string;
    licenceNumber: string;
    licenceJurisdiction: string;
    adminCcEmail: string;
    contactPhone: string;
    /** Lower case, without a leading "@". */
    emailDomains: string[];
    /** Upper case, or '' when the profile has none or has not loaded. */
    quoteNumberPrefix: string;
}

function text(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

export function normaliseQuotePrefix(value: unknown): string {
    const prefix = text(value);
    return PREFIX_RE.test(prefix) ? prefix.toUpperCase() : '';
}

/**
 * The demo business's own identity from its profile, or null when the
 * signed-in business is not a demo (the caller then keeps RJ Bird's values).
 */
export function demoBusinessIdentity(): DemoBusinessIdentity | null {
    if (!isDemoBusiness()) return null;
    let profile: any = null;
    try {
        profile = ownProfile(getInstanceOwnerCached());
    } catch {
        profile = null;
    }
    return identityFromProfile(profile);
}

/** The profile when it belongs to the signed-in business, else null. */
function ownProfile(profile: unknown): any {
    if (!profile || typeof profile !== 'object') return null;
    const tenantId = (profile as Record<string, unknown>).tenantId;
    try {
        return tenantId && tenantId === getActiveTenantId() ? profile : null;
    } catch {
        return null;
    }
}

/** A business's identity read from its profile, '' for each missing value. */
function identityFromProfile(profile: any): DemoBusinessIdentity {
    const legalName = text(profile?.legalName);
    const tradingName = text(profile?.tradingName);
    const emailDomains = (Array.isArray(profile?.emailDomains) ? profile.emailDomains : [])
        .map((domain: unknown) => text(domain).replace(/^@+/, '').toLowerCase())
        .filter(Boolean);
    return {
        legalName: legalName || tradingName,
        tradingName: tradingName || legalName,
        abn: text(profile?.abn),
        licenceNumber: text(profile?.licenceNumber),
        licenceJurisdiction: text(profile?.licenceJurisdiction),
        adminCcEmail: text(profile?.adminCcEmail),
        contactPhone: text(profile?.contactPhone),
        emailDomains,
        quoteNumberPrefix: normaliseQuotePrefix(profile?.quoteNumberPrefix),
    };
}

/**
 * This business's profile as far as the tab knows it: the cached profile, or
 * the last one this business read successfully (after a Settings save the
 * cache is empty until the refetch settles). null when neither exists, or
 * when the cache still holds another business's profile.
 */
function knownProfile(): any {
    try {
        return ownProfile(getInstanceOwnerCached({ orLastGood: true }));
    } catch {
        return null;
    }
}

/**
 * True only for RJ Bird's own profile, recognised by its stored name. Not for
 * a demo, and not for a business that is not known yet, whatever its prefix:
 * another business's initials can also be RBB (F11).
 */
function isKnownRjBird(): boolean {
    return !isDemoBusiness() && isRjBirdTenantProfile(knownProfile());
}

/**
 * The business name a document falls back to when its caller passes none:
 * "RJ Bird Building" for RJ Bird as today, the demo's trading name, or any
 * other business's own trading name (its legal name when it has none). '' for
 * a business whose profile is not known yet, never RJ Bird's name.
 */
export function fallbackBusinessName(): string {
    const demo = demoBusinessIdentity();
    if (demo) return demo.tradingName;
    if (isKnownRjBird()) return RJ_BIRD_FALLBACK_BUSINESS_NAME;
    const profile = knownProfile();
    return text(profile?.tradingName) || text(profile?.legalName);
}

/**
 * RJ Bird's wording for RJ Bird, and the neutral wording for every other
 * business: a demo, any other business, and one that is not known yet. For
 * interface copy (placeholders, hints, examples) that names RJ Bird's people,
 * clients, plans or numbers (F10).
 */
export function rjBirdOrDemoText(rjBird: string, neutral: string): string {
    return isKnownRjBird() ? rjBird : neutral;
}

/**
 * The example line under the Run Sheet search box. RJ Bird's word for word;
 * another business's names its own Quote # ("HTB260614"); a demo's names none.
 * A business that is not known yet gets its sign-in Quote # or none, also
 * while the cache still holds another business's profile.
 */
export function runSheetSearchTip(): string {
    if (isDemoBusiness()) return RUN_SHEET_SEARCH_TIP_DEMO;
    if (isKnownRjBird()) return RUN_SHEET_SEARCH_TIP_RJ_BIRD;
    const prefix = knownProfile() ? activeQuoteNumberPrefix() : getSessionQuoteNumberPrefix();
    return prefix ? runSheetSearchTipFor(prefix) : RUN_SHEET_SEARCH_TIP_DEMO;
}

export interface DocumentBusinessIdentity {
    /** RJ Bird, recognised by its stored name. */
    rjBird: boolean;
    /**
     * The business's own name, ABN and licence from its profile: RJ Bird's,
     * the demo's or any other business's. null for a business not known yet.
     */
    own: DemoBusinessIdentity | null;
    /**
     * The business's logo, the one its Stage 5 quote prints, on the colour of
     * its quote's logo bar (G7). null for a business with no logo and for a
     * business not known yet.
     */
    logo: DocumentLogo | null;
}

export interface DocumentLogo {
    src: string;
    /** The fill of the quote's logo bar: quoteAccentFor(profile).fill. */
    background: string;
}

/**
 * RJ Bird's block as the code printed it before it read RJ Bird's profile
 * (G4). Each value is used only when RJ Bird's profile lacks it.
 */
const RJ_BIRD_DOCUMENT_BLOCK = Object.freeze({
    legalName: 'RJ Bird Building Pty Ltd',
    abn: '48 606 543 382',
    licenceNumber: '285018C',
    licenceJurisdiction: 'NSW',
});

/**
 * An ABN in the route's stored form "XX XXX XXX XXX" (normaliseAbn in
 * backend/src/routes/tenant-profile.js). A value that is not 11 digits is
 * printed as stored.
 */
function documentAbn(value: string): string {
    const digits = value.replace(/\s+/g, '');
    if (!/^\d{11}$/.test(digits)) return value;
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
}

/**
 * RJ Bird's identity read from its profile (G4). RJ Bird's stored legal name
 * is "RJ BIRD BUILDING PTY LTD" (migration 030): a name that reads the same
 * prints as RJ Bird's documents always have, "RJ Bird Building Pty Ltd". A
 * different legal name prints as stored.
 */
function rjBirdIdentityFromProfile(profile: any): DemoBusinessIdentity {
    const own = identityFromProfile(profile);
    const fallback = RJ_BIRD_DOCUMENT_BLOCK;
    const sameName = !own.legalName
        || normaliseTenantIdentityName(own.legalName) === normaliseTenantIdentityName(fallback.legalName);
    return {
        ...own,
        legalName: sameName ? fallback.legalName : own.legalName,
        abn: own.abn ? documentAbn(own.abn) : fallback.abn,
        licenceNumber: own.licenceNumber || fallback.licenceNumber,
        licenceJurisdiction: own.licenceJurisdiction || fallback.licenceJurisdiction,
    };
}

/**
 * A logo a document may print: a path, an http(s) URL or an image data URI.
 * A path typed in Settings with any other scheme (javascript:) prints none.
 */
function printableLogo(src: string | null): string | null {
    const value = String(src ?? '').trim();
    if (!value) return null;
    const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(value)?.[1]?.toLowerCase();
    if (!scheme || scheme === 'http' || scheme === 'https') return value;
    return scheme === 'data' && /^data:image\//i.test(value) ? value : null;
}

/** The business's logo for a document, on its quote's logo-bar colour. */
function documentLogo(profile: any): DocumentLogo | null {
    const src = printableLogo(businessLogoSrc(profile));
    return src ? { src, background: quoteAccentFor(profile).fill } : null;
}

/**
 * Whose name, ABN, licence and logo a document prints (F8, G4, G7). Every
 * business, RJ Bird included, prints the values its own profile holds, the
 * way the Stage 5 quote reads them; RJ Bird falls back to its old literals
 * only for a value its profile lacks. The logo is the one the quote prints.
 * A business that is not known yet prints none, never RJ Bird's, and neither
 * does a profile of another business. `profile` defaults to the profile the
 * tab knows; a caller that can wait passes the one getInstanceOwner()
 * answers.
 */
export function documentBusinessIdentity(profile: unknown = knownProfile()): DocumentBusinessIdentity {
    if (isDemoBusiness()) {
        let demoProfile: any = null;
        try {
            demoProfile = ownProfile(getInstanceOwnerCached());
        } catch {
            demoProfile = null;
        }
        return { rjBird: false, own: identityFromProfile(demoProfile), logo: documentLogo(demoProfile) };
    }
    const mine = ownProfile(profile);
    if (isRjBirdTenantProfile(mine)) {
        return { rjBird: true, own: rjBirdIdentityFromProfile(mine), logo: documentLogo(mine) };
    }
    const own = identityFromProfile(mine);
    return own.legalName
        ? { rjBird: false, own, logo: documentLogo(mine) }
        : { rjBird: false, own: null, logo: null };
}
